import { useEffect } from "react";
import { apiFetch, token } from "../api/client";
import { useEvent } from "../context/EventProvider";
import { useSession } from "../auth/useSession";
import { useMode } from "../panel/mode";

/**
 * ═══ LE JOURNAL DE LA FICHE CONTACT ═══
 *
 * Ce que le participant fait sur le site s'écrit dans `ActionLogsUsers`, et le back office
 * l'affiche dans l'onglet Logs de sa fiche. Une seule porte :
 * `POST /v2/domains/:domainId/events/:eventId/action-logs` sur api-v1, qui prend l'auteur
 * dans le jeton et refuse d'écrire sur une autre fiche que la sienne ou celle de son
 * propre accompagnant.
 *
 * ⚠️ LE JOURNAL NE CASSE JAMAIS CE QU'IL RACONTE. Rien n'est attendu, aucune erreur ne
 * remonte : une ligne manquée vaut mieux qu'une inscription qui échoue pour elle.
 *
 * ⚠️ PAS DE LIGNE POUR L'ORGANISATEUR QUI CONFIGURE. Panneau « Configurer le site » ouvert,
 * il parcourt les écrans pour les régler, pas pour participer : `setOrganiserMode` est posé
 * par le châssis, et tout se tait tant qu'il dure. Pas de ligne non plus sans session.
 *
 * Les identifiants 1 à 8, 31 et 32 sont ceux de la V1 (même sens, historique continu) ;
 * 34 et suivants viennent de la migration apix `20261018120000_ActionLogs-actions-fo-v2`.
 */
export const ACTION = {
  homeAccess: 1,
  confirmationAccess: 2,
  liveAccess: 3,
  albumAccess: 4,
  photoDownload: 5,
  badgeDownload: 6,
  registration: 7,
  unregistration: 8,
  companyUpdate: 31,
  userUpdate: 32,
  signIn: 34,
  accountCreation: 35,
  payment: 36,
  companionAdd: 37,
  faceUpload: 39,
  faceRemoval: 40,
  registrationUpdate: 41,
  promoCode: 42,
  conferenceRegistration: 43,
  conferenceUnregistration: 44,
  invoiceDownload: 45,
  liveAnswer: 46,
  liveQuestion: 47,
  liveWord: 48,
  badgeAccess: 49,
} as const;
export type ActionKind = keyof typeof ACTION;

export type LogTarget = { domainId: number; eventId: number };

export type LogOptions = {
  /** La fiche où la ligne paraît : soi-même par défaut, ou son accompagnant. */
  userId?: number | null;
  companyId?: number | null;
  /** Le détail court de la colonne « Détail ». */
  label?: string | null;
};

let organiser = false;

/** Posé par le châssis : vrai tant que le panneau « Configurer le site » est ouvert. */
export function setOrganiserMode(on: boolean): void {
  organiser = on;
}

export function logAction(
  target: LogTarget | null | undefined,
  action: ActionKind,
  options: LogOptions = {},
): void {
  if (!target?.domainId || !target.eventId) return;
  if (organiser || !token.read()) return;
  const body: Record<string, unknown> = { actionId: ACTION[action] };
  if (options.userId) body.userId = options.userId;
  if (options.companyId) body.companyId = options.companyId;
  if (options.label) body.label = options.label;
  apiFetch(
    `/v2/domains/${target.domainId}/events/${target.eventId}/action-logs`,
    // `keepalive` : un téléchargement ou un départ vers Stripe ne doit pas l'avorter.
    { method: "POST", body, keepalive: true },
  ).catch(() => {
    /* le journal se tait : voir plus haut */
  });
}

/**
 * Les accès ne comptent qu'une fois par jour, et c'est le serveur qui tient la règle
 * (deux onglets, un autre appareil). Ce mémo ne fait qu'épargner la requête quand le
 * même onglet revient sur la page dans la journée.
 */
const accessed = new Set<string>();

export function useAccessLog(
  action: "homeAccess" | "confirmationAccess" | "liveAccess" | "albumAccess" | "badgeAccess",
  enabled = true,
): void {
  const event = useEvent();
  const signedIn = useSession();
  const mode = useMode();
  const domainId = event?.domainId;
  const eventId = event?.eventId;
  useEffect(() => {
    if (!enabled || !signedIn || mode !== "visitor" || !domainId || !eventId) return;
    const key = `${token.read()}|${eventId}|${action}|${new Date().toDateString()}`;
    if (accessed.has(key)) return;
    accessed.add(key);
    logAction({ domainId, eventId }, action);
  }, [enabled, signedIn, mode, domainId, eventId, action]);
}

/**
 * ═══ LA CONNEXION PAR LIEN ═══
 *
 * Le jeton d'un lien se range avant le premier rendu (`auth/link.ts`), quand l'événement
 * n'est pas encore connu : la connexion est mise de côté ici, puis notée par le châssis
 * dès que l'événement l'est. Le clic sur le lien lui-même ne se note pas, seule la
 * connexion qui suit.
 *
 * ⚠️ LE BOUTON « SE CONNECTER » DU BACK OFFICE passe par la même porte, avec un jeton
 * identique : rien ici ne les sépare. C'est le serveur qui écarte ce cas, à la ligne
 * « Se connecter · Fiche contact » que le BO vient d'écrire.
 */
let linkSignIn = false;

export function noteSignInByLink(): void {
  linkSignIn = true;
}

/** Le châssis lui passe l'événement qu'il tient déjà (`useEventState`). */
export function usePendingSignIn(event: LogTarget | null | undefined): void {
  const signedIn = useSession();
  const domainId = event?.domainId;
  const eventId = event?.eventId;
  useEffect(() => {
    if (!linkSignIn || !signedIn || !domainId || !eventId) return;
    linkSignIn = false;
    logAction({ domainId, eventId }, "signIn", { label: "lien" });
  }, [signedIn, domainId, eventId]);
}

/** Pour les tests : oublie les accès de l'onglet. */
export function forgetAccesses(): void {
  accessed.clear();
  linkSignIn = false;
}
